import {
  ensurePermissionsAndCheck,
  watchAbilities,
  type PermissionCheckInput,
} from '@/core/helpers/checkAbility';
import { DirectiveBinding, nextTick, VNode } from 'vue';

type VCanBinding = DirectiveBinding<string | string[]>;

interface VCanElementState {
  stopWatch: () => void;
  bindingKey: string;
  host: HTMLElement;
}

const elementState = new WeakMap<HTMLElement, VCanElementState>();

function toPermissionInput(binding: VCanBinding): PermissionCheckInput {
  return {
    value: binding.value,
    any: binding.modifiers.any,
    not: binding.modifiers.not,
  };
}

function bindingKey(binding: VCanBinding): string {
  const input = toPermissionInput(binding);
  const value = Array.isArray(input.value)
    ? input.value.join(',')
    : String(input.value ?? '');

  return `${value}:${input.any ? 'any' : 'all'}:${input.not ? 'not' : ''}`;
}

function findFirstElement(node: unknown): HTMLElement | null {
  if (node instanceof HTMLElement) {
    return node;
  }

  if (!node || typeof node !== 'object') {
    return null;
  }

  const elementNode = node as {
    el?: unknown;
    component?: { subTree?: unknown };
    children?: unknown[];
  };

  if (elementNode.el instanceof HTMLElement) {
    return elementNode.el;
  }

  const fromComponent = findFirstElement(elementNode.component?.subTree);
  if (fromComponent) {
    return fromComponent;
  }

  if (Array.isArray(elementNode.children)) {
    for (const child of elementNode.children) {
      const found = findFirstElement(child);
      if (found) {
        return found;
      }
    }
  }

  return null;
}

function resolveHostElement(el: unknown, vnode?: VNode): HTMLElement | null {
  if (el instanceof HTMLElement) {
    return el;
  }

  const fromVnode = findFirstElement(vnode?.el ?? vnode?.component?.subTree);
  if (fromVnode) {
    return fromVnode;
  }

  return findFirstElement(el);
}

function hideElement(el: HTMLElement): void {
  el.setAttribute('data-v-can-hidden', 'true');
  el.setAttribute('aria-hidden', 'true');
  el.style.setProperty('display', 'none', 'important');
}

function showElement(el: HTMLElement): void {
  el.removeAttribute('data-v-can-hidden');
  el.removeAttribute('aria-hidden');
  el.style.removeProperty('display');
}

async function applyVisibility(
  host: HTMLElement,
  binding: VCanBinding,
): Promise<void> {
  const input = toPermissionInput(binding);

  if (!input.value || (Array.isArray(input.value) && input.value.length === 0)) {
    showElement(host);
    return;
  }

  const allowed = await ensurePermissionsAndCheck(input);

  if (allowed) {
    showElement(host);
  } else {
    hideElement(host);
  }
}

function setupWatch(host: HTMLElement, binding: VCanBinding): void {
  const existing = elementState.get(host);
  const nextKey = bindingKey(binding);

  if (existing) {
    existing.bindingKey = nextKey;
    existing.host = host;
    return;
  }

  const stopWatch = watchAbilities(() => {
    void applyVisibility(host, binding);
  });

  elementState.set(host, { stopWatch, bindingKey: nextKey, host });
}

function teardownWatch(host: HTMLElement): void {
  const existing = elementState.get(host);
  existing?.stopWatch();
  elementState.delete(host);
}

async function runDirective(
  el: unknown,
  binding: VCanBinding,
  vnode?: VNode,
): Promise<void> {
  await nextTick();

  const host = resolveHostElement(el, vnode);
  if (!host) {
    return;
  }

  hideElement(host);
  setupWatch(host, binding);
  await applyVisibility(host, binding);
}

/**
 * @description v-can directive for conditional rendering based on permissions
 */
export default {
  beforeMount(el: unknown, binding: VCanBinding, vnode: VNode) {
    const host = resolveHostElement(el, vnode);
    if (host) {
      hideElement(host);
    }
  },

  mounted(el: unknown, binding: VCanBinding, vnode: VNode) {
    void runDirective(el, binding, vnode);
  },

  updated(el: unknown, binding: VCanBinding, vnode: VNode) {
    void nextTick().then(async () => {
      const host = resolveHostElement(el, vnode);
      if (!host) {
        return;
      }

      const existing = elementState.get(host);
      const nextKey = bindingKey(binding);

      if (!existing || existing.bindingKey !== nextKey) {
        teardownWatch(host);
        setupWatch(host, binding);
      }

      await applyVisibility(host, binding);
    });
  },

  beforeUnmount(el: unknown, _binding: VCanBinding, vnode?: VNode) {
    const host = resolveHostElement(el, vnode);
    if (host) {
      teardownWatch(host);
    }
  },
};
